import { useState } from 'react';
import { Sparkles, Zap, Mail, Phone, TrendingUp, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useAIAssistant } from '@/hooks/useAIAssistant';
import { toast } from 'sonner';

interface AIQuickActionsProps {
  entityType: 'lead' | 'deal' | 'contact' | 'call';
  entity: any;
  onAction?: (action: string, result: string) => void;
}

export function AIQuickActions({ entityType, entity, onAction }: AIQuickActionsProps) {
  const [isLoading, setIsLoading] = useState<string | null>(null);
  const { scoreLead, generateEmail, analyzeCall, getDealInsights } = useAIAssistant();

  const handleAction = async (action: string) => {
    setIsLoading(action);
    let result: string | null = null;

    try {
      switch (action) {
        case 'score':
          if (entityType === 'lead') {
            result = await scoreLead(entity);
          } else if (entityType === 'deal') {
            result = await getDealInsights(entity, entity.stage_name || 'Unknown', entity.account_name || 'Unknown');
          }
          break;
        case 'email':
          const name = entity.first_name 
            ? `${entity.first_name} ${entity.last_name || ''}`.trim()
            : entity.name || 'there';
          result = await generateEmail({
            recipientName: name,
            companyName: entity.company_name || entity.account_name || 'your company',
            emailType: 'follow_up',
          });
          break;
        case 'analyze':
          if (entityType === 'call') {
            result = await analyzeCall(entity);
          }
          break;
        case 'insights':
          if (entityType === 'deal') {
            result = await getDealInsights(entity, entity.stage_name || 'Unknown', entity.account_name || 'Unknown');
          }
          break;
      }

      if (result) {
        toast.success('AI action completed');
        onAction?.(action, result);
      }
    } catch (error) {
      console.error('AI action error:', error);
    } finally {
      setIsLoading(null);
    }
  };

  const getActionsForEntity = () => {
    switch (entityType) {
      case 'lead':
        return [
          { id: 'score', label: 'AI Score', icon: TrendingUp },
          { id: 'email', label: 'Generate Email', icon: Mail },
        ];
      case 'deal':
        return [
          { id: 'insights', label: 'Deal Insights', icon: TrendingUp },
          { id: 'email', label: 'Generate Email', icon: Mail },
        ];
      case 'contact':
        return [
          { id: 'email', label: 'Generate Email', icon: Mail },
        ];
      case 'call':
        return [
          { id: 'analyze', label: 'Analyze Call', icon: Phone },
        ];
      default:
        return [];
    }
  };

  const actions = getActionsForEntity();

  if (actions.length === 0) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2 h-8">
          {isLoading ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Sparkles className="h-3.5 w-3.5" />
          )}
          AI Actions
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48 bg-popover border border-border z-50">
        <DropdownMenuLabel className="flex items-center gap-2">
          <Zap className="h-3.5 w-3.5 text-primary" />
          Quick AI Actions
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {actions.map((action) => (
          <DropdownMenuItem
            key={action.id}
            onClick={() => handleAction(action.id)}
            disabled={isLoading === action.id}
            className="gap-2 cursor-pointer"
          >
            {isLoading === action.id ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <action.icon className="h-4 w-4" />
            )}
            {action.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
